import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion } from '../api/tipos';
import { Cargando, Dialogo, Error } from '../componentes/comunes';
import { useSesion } from '../sesion';

/**
 * Tarifas de iguala.
 *
 * El precio de una iguala está pactado y depende de dos cosas: el tipo de
 * servicio y el formato de la tienda. Una Mi Bodega no cuesta lo mismo que
 * un Supercenter aunque el servicio se llame igual.
 *
 * Aquí se captura esa combinación, una vez. De ahí la lee cada prefactura
 * de iguala, y por eso dos personas facturando la misma sucursal el mismo
 * mes escriben el mismo número.
 *
 * Las tarifas llevan vigencia y no se borran: al capturar una nueva se
 * cierra la anterior. Volver a generar el archivo de una prefactura de
 * hace tres meses tiene que dar el importe que se timbró, no el de hoy.
 */

interface Tarifa {
  id: string;
  tipoIguala: Opcion;
  formatoTienda: Opcion;
  precio: number;
  periodicidad: 'mensual' | 'bimestral' | 'trimestral';
  vigenteDesde: string;
  vigenteHasta: string | null;
  nota: string | null;
  vigente: boolean;
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/** Hoy en formato de campo de fecha, que es lo que espera el input. */
/**
 * Con las partes locales, no con `toISOString`, que da la fecha en UTC:
 * capturando a partir de las seis de la tarde en México ese atajo
 * devuelve mañana, y la tarifa nacería con un día de retraso.
 */
function hoy(): string {
  const ahora = new Date();
  const mes = String(ahora.getMonth() + 1).padStart(2, '0');
  const dia = String(ahora.getDate()).padStart(2, '0');

  return `${ahora.getFullYear()}-${mes}-${dia}`;
}

export default function TarifasIguala() {
  const { puede } = useSesion();
  const [tarifas, setTarifas] = useState<Tarifa[] | null>(null);
  const [tipos, setTipos] = useState<Opcion[]>([]);
  const [formatos, setFormatos] = useState<Opcion[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [capturando, setCapturando] = useState(false);
  const [soloVigentes, setSoloVigentes] = useState(true);

  const cargar = useCallback(() => {
    api.get<Tarifa[]>('/facturacion/tarifas')
      .then(setTarifas)
      .catch(setError);
  }, []);

  useEffect(() => {
    cargar();
    api.get<Opcion[]>('/catalogos/tipos-iguala/opciones').then(setTipos).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/formatos-tienda/opciones')
      .then(setFormatos)
      .catch(() => undefined);
  }, [cargar]);

  if (!tarifas) return <Cargando que="Cargando tarifas…" />;

  const visibles = soloVigentes ? tarifas.filter((t) => t.vigente) : tarifas;
  const puedeCapturar = puede('catalogos', 'crear');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Tarifas de iguala</h1>
          <p className="sub">
            El precio pactado de la iguala, por tipo de servicio y formato
            de tienda. De aquí lo toma cada prefactura de iguala. Al
            capturar una tarifa nueva se cierra la anterior de la misma
            combinación: las viejas no se borran porque las facturas que
            las usaron siguen existiendo.
          </p>
        </div>
        {puedeCapturar && (
          <button className="primario" onClick={() => setCapturando(true)}>
            Capturar tarifa
          </button>
        )}
      </div>

      <Error error={error} />

      <div className="panel">
        <div className="controles">
          <label className="campo inline">
            <input
              type="checkbox" checked={soloVigentes}
              onChange={(e) => setSoloVigentes(e.target.checked)}
            />
            Sólo las vigentes hoy
          </label>
        </div>

        {visibles.length === 0 ? (
          <div className="vacio">
            No hay tarifas capturadas. Mientras no las haya, el precio de la
            iguala se teclea en cada prefactura.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Tipo de iguala</th>
                <th>Formato de tienda</th>
                <th className="numero">Precio</th>
                <th>Periodo</th>
                <th>Vigencia</th>
                <th>Nota</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((t) => (
                <tr key={t.id}>
                  <td>{t.tipoIguala?.nombre ?? '—'}</td>
                  <td>{t.formatoTienda?.nombre ?? '—'}</td>
                  <td className="numero">{moneda(t.precio)}</td>
                  <td>{t.periodicidad}</td>
                  <td>
                    {fechaCorta(t.vigenteDesde)}
                    {t.vigenteHasta
                      ? ` — ${fechaCorta(t.vigenteHasta)}`
                      : <span className="conteo"> — vigente</span>}
                  </td>
                  <td className="conteo">{t.nota ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {capturando && (
        <FormularioTarifa
          tipos={tipos}
          formatos={formatos}
          onCerrar={() => setCapturando(false)}
          onGuardar={() => { setCapturando(false); cargar(); }}
        />
      )}
    </>
  );
}

function FormularioTarifa({
  tipos, formatos, onCerrar, onGuardar,
}: {
  tipos: Opcion[];
  formatos: Opcion[];
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const [tipoIgualaId, setTipoIgualaId] = useState('');
  const [formatoTiendaId, setFormatoTiendaId] = useState('');
  const [precio, setPrecio] = useState('');
  const [periodicidad, setPeriodicidad] = useState('mensual');
  const [vigenteDesde, setVigenteDesde] = useState(hoy());
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const listo = tipoIgualaId && formatoTiendaId && Number(precio) > 0 && vigenteDesde;

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      await api.post('/facturacion/tarifas', {
        tipoIgualaId,
        formatoTiendaId,
        precio: Number(precio),
        periodicidad,
        vigenteDesde,
        nota: nota.trim() || undefined,
      });
      onGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialogo
      titulo="Capturar tarifa de iguala"
      onCerrar={onCerrar}
      ancho
      pie={(
        <>
          <button onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={!listo || guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="tipo">Tipo de iguala</label>
          <select id="tipo" value={tipoIgualaId} onChange={(e) => setTipoIgualaId(e.target.value)}>
            <option value="">Elige…</option>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="formato">Formato de tienda</label>
          <select
            id="formato" value={formatoTiendaId}
            onChange={(e) => setFormatoTiendaId(e.target.value)}
          >
            <option value="">Elige…</option>
            {formatos.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="precio">Precio</label>
          <input
            id="precio" type="number" min="0.01" step="0.01"
            value={precio} onChange={(e) => setPrecio(e.target.value)}
          />
          <span className="ayuda">Sin IVA. Es lo que va en el concepto.</span>
        </div>

        <div className="campo">
          <label htmlFor="periodo">Periodo que cubre</label>
          <select
            id="periodo" value={periodicidad}
            onChange={(e) => setPeriodicidad(e.target.value)}
          >
            <option value="mensual">Mensual</option>
            <option value="bimestral">Bimestral</option>
            <option value="trimestral">Trimestral</option>
          </select>
          <span className="ayuda">
            El mismo servicio facturado mensual o trimestral es otro importe.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="desde">Vigente desde</label>
          <input
            id="desde" type="date"
            value={vigenteDesde} onChange={(e) => setVigenteDesde(e.target.value)}
          />
          <span className="ayuda">
            La tarifa anterior de esta combinación se cierra el día antes.
          </span>
        </div>

        <div className="campo ancho-total">
          <label htmlFor="nota">Nota</label>
          <input
            id="nota" value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="De dónde sale este precio: contrato, renovación, acuerdo…"
          />
        </div>
      </div>
    </Dialogo>
  );
}
